import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { ClientProductCarrierWithCarrier } from "../types/rfpApi";

interface UseRfpProductCoveragesOptions<TSelected> {
  rfpId: string;
  enabled?: boolean;
  select?: (data: ClientProductCarrierWithCarrier[]) => TSelected;
}

// Returns the React Query key for RFP product coverages. Use this to reference in cache
export const getRfpProductCoveragesQueryKey = (
  rfpId: string,
): ["rfps", string, "product-coverages"] => [
  "rfps",
  rfpId,
  "product-coverages",
];

// useRfpProductCoverages with selector overload
export function useRfpProductCoverages<TSelected>(options: {
  rfpId: string;
  enabled?: boolean;
  select: (data: ClientProductCarrierWithCarrier[]) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useRfpProductCoverages with default select for all product coverages data
export function useRfpProductCoverages(options: {
  rfpId: string;
  enabled?: boolean;
}): UseQueryResult<ClientProductCarrierWithCarrier[], Error>;

// Hook to fetch RFP product coverages given an RFP ID
// Pass in a React Query select to transform the data when reading from the cache (the original data is cached; select is applied on read)
export function useRfpProductCoverages<
  TSelected = ClientProductCarrierWithCarrier[],
>({
  rfpId,
  enabled = true,
  select,
}: UseRfpProductCoveragesOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axios = useApi();
  return useQuery<ClientProductCarrierWithCarrier[], Error, TSelected>({
    queryKey: getRfpProductCoveragesQueryKey(rfpId),
    enabled: enabled && !!rfpId,
    select,
    queryFn: () =>
      axios
        .get<
          ClientProductCarrierWithCarrier[]
        >(`brokerportal/v1/rfps/${rfpId}/product-coverages`)
        .then((res) => res.data),
  });
}
